/**
 * Data API を利用してコンテンツデータ、記事を複製します。一覧画面での複製にも対応しています。
 *
 * Updated: 2020/02/18
 *
 * @requires mtappVars bootstrap.Modal
 * @param {Object} options
 * @param {String} options.additionalText 複製元のタイトルにつけるテキストを設定します（初期値：'のコピー'）。
 * @param {String} options.basenameFieldId アーカイブマッピングで出力されるファイル名に利用しているフィールドを指定します。（初期値：'basename'）。
 * @param {String} options.basenameAdditionalText basenameFieldId オプションで指定したフィールドの値に付け足すテキストを設定します（初期値：'-copy'）。
 * @param {Number} options.contentDataTitleFieldId コンテンツデータの場合はタイトルとなるフィールドの ID をセットします。データ識別子をそのまま利用している場合は設定不要です。
 * @param {String} options.contentDataTitleFieldLabel コンテンツデータの場合はタイトルとなるフィールドのラベルをセットします。
 * @param {Function} options.cbModifyContent 複製元のコンテンツを複製する前に編集する関数を設定します。この関数の第一引数には複製するコンテンツのオブジェクト、第二引数にはオリジナルのオブジェクトが渡されます。編集後のオブジェクトを return します。
 */
import { loadingImage } from './loadingImage.js';
import { toJSONString } from './toJSONString.js';
import { modalMsg } from './modalMsg.js';
import { modalConfirm } from './modalConfirm.js';
import { deepCopy } from './deepCopy.js';
import { getDataApiUrl } from './getDataApiUrl.js';

export function duplicateContent(options) {

    const op = {
        additionalText: 'のコピー',
        basenameFieldId: 'basename',
        basenameAdditionalText: '-copy',
        contentDataTitleFieldId: 0,
        contentDataTitleFieldLabel: 'タイトル',
        cbModifyContent: null,
        ...options
    };

    const dataApiUrl = getDataApiUrl();

    // @ts-ignore
    const mtappVars = window.mtappVars;

    // すでに duplicateContent が適用されている場合は何もしない
    mtappVars.__system = mtappVars.__system || {};
    if (typeof mtappVars.__system.hasDuplicateFunction === 'undefined') {
        mtappVars.__system.hasDuplicateFunction = true;
    } else {
        return;
    }

    // 複製ボタンを作成、挿入、取得
    const duplicateButton = '<button type="button" id="mtapp-duplicate-button" class="btn btn-warning text-white">複製</button>';

    // コンテンツデータ・記事編集画面の場合
    if (['edit-content-type-data', 'edit-entry'].includes(mtappVars.screen_id)) {
        const entryPublishingWidget = document.querySelector('#entry-publishing-widget');
        if (entryPublishingWidget) {
            const btnPrimary = entryPublishingWidget.querySelector('.btn-primary[name="status"]');
            if (btnPrimary && btnPrimary.parentElement) {
                btnPrimary.parentElement.insertAdjacentHTML('beforeend', duplicateButton);
            }
        }
    }
    // コンテンツデータ・記事一覧画面の場合
    else if (['list-content_data', 'list-entry'].includes(mtappVars.screen_id)) {
        const deleteButton = document.querySelector('#actions-bar-top [data-action-id="delete"]');
        if (deleteButton) {
            deleteButton.insertAdjacentHTML('afterend', duplicateButton);
        } else {
            const handleListReady = function () {
                const deleteButton = document.querySelector('#actions-bar-top [data-action-id="delete"]');
                if (deleteButton) {
                    deleteButton.insertAdjacentHTML('afterend', duplicateButton);
                }
                window.removeEventListener('listReady.mtapp.duplicate', handleListReady);
            };
            window.addEventListener('listReady.mtapp.duplicate', handleListReady);
        }
    }

    // DOM を取得
    const body = document.body;
    const modalElement = document.getElementById('mtapp-modal-duplicate');
    // @ts-ignore
    const modal = new (window.bootstrap).Modal(modalElement);
    const duplicateModalTitleLabel = document.querySelector('#mtapp-modal-duplicate-title-label');
    const duplicateModalNewTitle = document.querySelector('#mtapp-modal-duplicate-new-title');
    const duplicateModalSubmit = document.querySelector('#mtapp-modal-duplicate-submit');

    // 複製を実行
    body.addEventListener('mtapp.duplicate', function (event) {
        const customEvent = /** @type {CustomEvent} */ (event);
        const { collection, confirm } = customEvent.detail || {};

        if (!Array.isArray(collection) || collection.length === 0) {
            console.error('Invalid arguments');
            return alert('Invalid arguments');
        }

        const doDuplicate = async function () {
            let updatedCount = 0;

            loadingImage('show');

            // コンテンツデータの場合
            if (['edit-content-type-data', 'list-content_data'].includes(mtappVars.screen_id)) {
                // 非同期でコンテンツデータを順次複製
                for (let index = 0; index < collection.length; index++) {
                    const cd = collection[index];

                    try {
                        // basenameの更新
                        if (op.basenameFieldId === 'basename') {
                            cd.basename += op.basenameAdditionalText;
                        } else if (Number(op.basenameFieldId) > 0) {
                            for (const field of cd.data) {
                                if (Number(field.id) === Number(op.basenameFieldId)) {
                                    field.data += op.basenameAdditionalText;
                                    break;
                                }
                            }
                        }

                        const newCd = toJSONString(cd);

                        // MovableType Data API v6を使用してコンテンツデータを作成
                        const apiUrl = `${dataApiUrl}/sites/${mtappVars.blog_id}/contentTypes/${mtappVars.content_type_id}/data`;

                        const formData = new FormData();
                        formData.append('content_data', newCd);

                        const response = await fetch(apiUrl, {
                            method: 'POST',
                            body: formData
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }

                        const responseData = await response.json();

                        // 成功した場合
                        if (responseData.id) {
                            updatedCount++;

                            // すべて終了した場合
                            if (updatedCount === collection.length) {
                                if (updatedCount === 1) {
                                    const url = mtappVars.adminScript +
                                        '?__mode=view&content_type_id=' +
                                        mtappVars.content_type_id +
                                        '&_type=content_data&id=' +
                                        responseData.id +
                                        '&type=content_data_' +
                                        mtappVars.content_type_id +
                                        '&blog_id=' +
                                        mtappVars.blog_id;
                                    location.href = url;
                                } else {
                                    const url = mtappVars.adminScript +
                                        '?__mode=list&_type=content_data&type=content_data_' +
                                        mtappVars.content_type_id +
                                        '&blog_id=' +
                                        mtappVars.blog_id;
                                    location.href = url;
                                }
                            }
                        }

                    } catch (error) {
                        console.error(`コンテンツデータ ${index} の複製エラー:`, error);
                        loadingImage('hide');
                        modalMsg({
                            type: 'error',
                            title: 'エラー',
                            content: `コンテンツデータの複製に失敗しました: ${error.message}`
                        });
                        return;
                    }
                }
            }
            // 記事の場合
            else if (['edit-entry', 'list-entry'].includes(mtappVars.screen_id)) {
                // 非同期で記事を順次複製
                for (let index = 0; index < collection.length; index++) {
                    const entry = collection[index];

                    try {
                        // basenameの更新
                        if (op.basenameFieldId.includes('field.')) {
                            for (const customField of entry.customFields) {
                                if (customField.basename === op.basenameFieldId.replace(/field./, '')) {
                                    if (customField.value) {
                                        customField.value += op.basenameAdditionalText;
                                    } else {
                                        customField.value = op.basenameAdditionalText;
                                    }
                                    break;
                                }
                            }
                        } else if (Object.prototype.hasOwnProperty.call(entry, op.basenameFieldId)) {
                            entry[op.basenameFieldId] += op.basenameAdditionalText;
                        }

                        const newEntry = toJSONString(entry);

                        // MovableType Data API v6を使用して記事を作成
                        const apiUrl = `${dataApiUrl}/sites/${mtappVars.blog_id}/entries`;

                        const formData = new FormData();
                        formData.append('entry', newEntry);
                        formData.append('publish', '0'); // 下書きとして作成

                        const response = await fetch(apiUrl, {
                            method: 'POST',
                            headers: {
                                'X-MT-Authorization': `MTAuth accessToken=${mtappVars.accessToken || ''}`
                            },
                            body: formData
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }

                        const responseData = await response.json();

                        // 成功した場合
                        if (responseData.id) {
                            updatedCount++;

                            // すべて終了した場合
                            if (updatedCount === collection.length) {
                                if (updatedCount === 1) {
                                    const url = mtappVars.adminScript +
                                        '?__mode=view&_type=entry&blog_id=' +
                                        mtappVars.blog_id +
                                        '&id=' +
                                        responseData.id;
                                    location.href = url;
                                } else {
                                    const url = mtappVars.adminScript + '?__mode=list&_type=entry&blog_id=' + mtappVars.blog_id;
                                    location.href = url;
                                }
                            }
                        }

                    } catch (error) {
                        loadingImage('hide');
                        modalMsg({
                            type: 'error',
                            title: 'エラー',
                            content: `記事の複製に失敗しました: ${error.message}`
                        });
                        return;
                    }
                }
            }
        };

        if (confirm) {
            modalConfirm({
                type: 'warning',
                title: '確認',
                content: '複製しますか？',
                okButtonLabel: 'OK',
                cancelButtonLabel: 'キャンセル',
                callback: function () {
                    doDuplicate();
                }
            });
        } else {
            doDuplicate();
        }
    });
    // END 複製を実行

    // ウィジェットやアクションバーの複製ボタンをクリックしたときの処理
    body.addEventListener('click', async function (event) {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target && target.id === 'mtapp-duplicate-button') {
            let ids = [];

            if (duplicateModalTitleLabel) {
                duplicateModalTitleLabel.innerHTML = op.contentDataTitleFieldLabel;
            }

            // 編集画面で実行された場合
            if (['edit-content-type-data', 'edit-entry'].includes(mtappVars.screen_id)) {
                if (!mtappVars.id) {
                    console.error('mtappVars.id が存在しない');
                    modalMsg({
                        type: 'error',
                        title: 'エラー',
                        content: '複製するコンテンツが保存されていません。'
                    });
                    return;
                }
                ids.push(mtappVars.id);
            }
            // 一覧画面で実行された場合
            else if (['list-content_data', 'list-entry'].includes(mtappVars.screen_id)) {
                const checkedInputs = document.querySelectorAll('[data-is="list-table-body"] [name="id"]:checked');
                checkedInputs.forEach(function (input) {
                    ids.push(/** @type {HTMLInputElement} */ (input).value);
                });
                if (ids.length === 0) {
                    console.error('選択されたコンテンツがない');
                    modalMsg({
                        type: 'error',
                        title: 'エラー',
                        content: '複製するコンテンツが選択されていません。'
                    });
                    return;
                }
            }

            // コンテンツデータの場合
            if (['edit-content-type-data', 'list-content_data'].includes(mtappVars.screen_id)) {
                loadingImage('show');

                // コンテンツデータを取得
                try {
                    // MovableType Data API v6を使用してコンテンツデータを取得
                    const apiUrl = `${dataApiUrl}/sites/${mtappVars.blog_id}/contentTypes/${mtappVars.content_type_id}/data`;
                    const queryParams = new URLSearchParams({
                        limit: '1000',
                        includeIds: ids.join(',')
                    });

                    const response = await fetch(`${apiUrl}?${queryParams}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-MT-Authorization': `MTAuth accessToken=${mtappVars.accessToken || ''}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const responseData = await response.json();

                    loadingImage('hide');

                    // items が空の場合
                    if (!responseData.items || responseData.items.length === 0) {
                        console.error('responseData.items が空');
                        modalMsg({
                            type: 'error',
                            title: 'エラー',
                            content: '複製元のデータが見つかりません。'
                        });
                        return;
                    }

                    loadingImage('hide');

                    // 取得したすべてのコンテンツデータを調整
                    let currentTitle = '';
                    let updateContentData = responseData.items.map(function (cd) {
                        const originalCd = deepCopy(cd);
                        // 共通の処理
                        delete cd.id;
                        cd.status = 'Draft';
                        // 日時をリセット
                        delete cd.date;
                        delete cd.createdDate;
                        delete cd.modifiedDate;
                        // タイトルを調整
                        if (op.contentDataTitleFieldId) {
                            op.contentDataTitleFieldId = Number(op.contentDataTitleFieldId);
                            for (let i = 0; i < cd.data.length; i++) {
                                if (Number(cd.data[i].id) === op.contentDataTitleFieldId) {
                                    currentTitle = cd.data[i].data + op.additionalText;
                                    cd.data[i].data = currentTitle;
                                }
                            }
                        } else {
                            currentTitle = cd.label + op.additionalText;
                        }
                        cd.label = currentTitle;

                        // コールバックでの処理
                        if (typeof op.cbModifyContent === 'function') {
                            cd = op.cbModifyContent(cd, originalCd);
                        }

                        return cd;
                    });

                    // コンテンツデータ編集画面で実行された場合
                    if (mtappVars.screen_id === 'edit-content-type-data') {
                        // モーダルに複製後のタイトルのセット
                        if (duplicateModalNewTitle) {
                            /** @type {HTMLInputElement} */ (duplicateModalNewTitle).value = currentTitle;
                        }

                        // 複製モーダルを表示
                        modal.show();

                        // モーダルの複製ボタンがクリックされたときの処理
                        if (duplicateModalSubmit) {
                            const handleSubmitClick = function () {
                                // 編集したタイトルをセット
                                if (op.contentDataTitleFieldId && duplicateModalNewTitle) {
                                    op.contentDataTitleFieldId = Number(op.contentDataTitleFieldId);
                                    for (let i = 0; i < updateContentData[0].data.length; i++) {
                                        if (Number(updateContentData[0].data[i].id) === op.contentDataTitleFieldId) {
                                            updateContentData[0].data[i].data = /** @type {HTMLInputElement} */ (duplicateModalNewTitle).value;
                                        }
                                    }
                                }
                                if (duplicateModalNewTitle) {
                                    updateContentData[0].label = /** @type {HTMLInputElement} */ (duplicateModalNewTitle).value;
                                }

                                modal.hide();
                                const customEvent = new CustomEvent('mtapp.duplicate', {
                                    detail: { collection: updateContentData, confirm: false }
                                });
                                body.dispatchEvent(customEvent);
                            };

                            duplicateModalSubmit.removeEventListener('click', handleSubmitClick);
                            duplicateModalSubmit.addEventListener('click', handleSubmitClick);
                        }
                    }
                    // コンテンツデータ一覧画面で実行された場合
                    else if (mtappVars.screen_id === 'list-content_data') {
                        const customEvent = new CustomEvent('mtapp.duplicate', {
                            detail: { collection: updateContentData, confirm: true }
                        });
                        body.dispatchEvent(customEvent);
                    }

                } catch (error) {
                    console.error('=== コンテンツデータ取得エラー ===');
                    console.error('error:', error);
                    loadingImage('hide');
                    modalMsg({
                        type: 'error',
                        title: 'エラー',
                        content: `コンテンツデータの取得に失敗しました: ${error.message}`
                    });
                    return;
                }
            }
            // 記事の場合
            else if (['edit-entry', 'list-entry'].includes(mtappVars.screen_id)) {
                loadingImage('show');

                try {
                    // MovableType Data API v6を使用して記事を取得
                    const apiUrl = `${dataApiUrl}/sites/${mtappVars.blog_id}/entries`;
                    const queryParams = new URLSearchParams({
                        limit: '1000',
                        includeIds: ids.join(',')
                    });

                    const response = await fetch(`${apiUrl}?${queryParams}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const responseData = await response.json();

                    loadingImage('hide');

                    // items が空の場合
                    if (!responseData.items || responseData.items.length === 0) {
                        modalMsg({
                            type: 'error',
                            title: 'エラー',
                            content: '複製元のデータが見つかりません。'
                        });
                        return;
                    }

                    // 取得したすべての記事を調整
                    let currentTitle = '';

                    let updateContentData = responseData.items.map(function (entry) {
                        const originalEntry = deepCopy(entry);
                        // 共通の処理
                        delete entry.id;
                        entry.status = 'Draft';
                        // タイトルを調整
                        currentTitle = entry.title + op.additionalText;
                        entry.title = currentTitle;
                        // 日時をリセット
                        delete entry.date;
                        delete entry.createdDate;
                        delete entry.modifiedDate;
                        // コールバックでの処理
                        if (typeof op.cbModifyContent === 'function') {
                            entry = op.cbModifyContent(entry, originalEntry);
                        }

                        return entry;
                    });

                    // 記事編集画面で実行された場合
                    if (mtappVars.screen_id === 'edit-entry') {
                        // モーダルに複製後のタイトルのセット
                        if (duplicateModalNewTitle) {
                            /** @type {HTMLInputElement} */ (duplicateModalNewTitle).value = currentTitle;
                        }

                        // 複製モーダルを表示
                        modal.show();

                        // モーダルの複製ボタンがクリックされたときの処理
                        if (duplicateModalSubmit) {
                            const handleSubmitClick = function () {
                                // 編集したタイトルをセット
                                if (duplicateModalNewTitle) {
                                    updateContentData[0].title = /** @type {HTMLInputElement} */ (duplicateModalNewTitle).value;
                                }

                                modal.hide();
                                const customEvent = new CustomEvent('mtapp.duplicate', {
                                    detail: { collection: updateContentData, confirm: false }
                                });
                                body.dispatchEvent(customEvent);
                            };

                            duplicateModalSubmit.removeEventListener('click', handleSubmitClick);
                            duplicateModalSubmit.addEventListener('click', handleSubmitClick);
                        }
                    }
                    // 記事一覧画面で実行された場合
                    else if (mtappVars.screen_id === 'list-entry') {
                        const customEvent = new CustomEvent('mtapp.duplicate', {
                            detail: { collection: updateContentData, confirm: true }
                        });
                        body.dispatchEvent(customEvent);
                    }

                } catch (error) {
                    loadingImage('hide');
                    modalMsg({
                        type: 'error',
                        title: 'エラー',
                        content: `記事の取得に失敗しました: ${error.message}`
                    });
                    return;
                }
            }
        }
    });
}
